import {Component, computed, effect, input, model, signal, inject} from '@angular/core';
import {ReactiveFormsModule, FormGroup} from '@angular/forms';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {Panel} from 'primeng/panel';
import {NgClass} from '@angular/common';
import {TableModule} from 'primeng/table';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  AppCurrencyPipe,
  ImplementationActivityFeeResponse,
  ImplementationYearlyFee,
  ImplementationFunctionFeeResponse,
} from '@frontend/shared';
import {PanelHeaderComponent} from '../../../../../../../../shared/components/header/panel-header/panel-header.component';
import {FormControlsConfig} from '../../../step-8-1-proposal-parameter/components/abstract-form-directive';
import {ModuleFunction} from '@frontend/directus';
import {toSignal} from '@angular/core/rxjs-interop';
import {ImplementationFeesContainer, RowType, FeeRowVM} from './implementation-fees.types';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {CommentModalButtonComponent} from '../../../../../../../../shared/components/dialog/comment-modal-button/comment-modal-button-reactive/comment-modal-button.component';
import {Button} from 'primeng/button';
import {TableExpandControlsComponent} from '../../../../../../../../shared/components/table/table-expand-controls/table-expand-controls.component';
import {UnitHourlyPipe} from './unit-hourly.pipe';
import {SumFees} from '../step-8-2-3-rfq-preview-price-list.types';
import {AbstractFormArrayDirective} from '../../../step-8-1-proposal-parameter/components/abstract-form-array-directive';
import {startWith} from 'rxjs';
import {FormFieldNumberComponent} from '../../../../../../../../shared/components/inputs/form-field/form-field-number/form-field-number.component';
import {RfqFeeCategoryService} from '../../../../../../../../shared/services/rfq-fee-category.service';

@Component({
  selector: 'app-implementation-fees-module',
  imports: [
    ReactiveFormsModule,
    Panel,
    NgClass,
    PanelHeaderComponent,
    TableModule,
    TranslocoPipe,
    AppCurrencyPipe,
    TranslateDirectusPipe,
    ProjectStepSaveButtonComponent,
    CommentModalButtonComponent,
    Button,
    TableExpandControlsComponent,
    UnitHourlyPipe,
    FormFieldNumberComponent,
  ],
  templateUrl: './implementation-fees.component.html',
  styleUrl: './implementation-fees.component.scss',
  providers: [TranslateDirectusPipe],
})
export class ImplementationFeesComponent extends AbstractFormArrayDirective<
  ImplementationFeesContainer,
  ImplementationActivityFeeResponse
> {
  protected readonly feeCategories = inject(RfqFeeCategoryService);
  moduleFunctionMap = input.required<Map<number, ModuleFunction>>();

  private expandedGroupIds = signal<Set<number>>(new Set());

  private static readonly FORM_CONTROLS: FormControlsConfig<ImplementationFeesContainer> = {
    locale: [''],
    currencyCode: [''],
    scopeYears: [[]],
    data: [[]],
  };

  implementationActivities = toSignal(this.directusService.loadImplementationActivities(), {
    initialValue: [],
  });

  private formValue = toSignal(this.theForm.valueChanges.pipe(startWith(this.theForm.value)));

  protected readonly feeRows = computed<FeeRowVM[]>(() => {
    this.formValue();

    const implementationFees = this.implementationActivities();
    const fnMap = this.moduleFunctionMap();
    const current = this.inputObject();
    const formData = this.theForm.get('data')?.value ?? [];
    const expanded = this.expandedGroupIds();

    if (!current?.data?.length) return [];

    return current.data.flatMap(
      (implementationActivityFee: ImplementationActivityFeeResponse, index: number) => {
        const groupId = index + 1;
        const currentFee = implementationFees.find(
          (m) => m.id === implementationActivityFee.implementationActivityDirectusUuid,
        );

        const formRow = formData[index];
        const formHourlyRate = Number(formRow?.hourlyRate ?? implementationActivityFee.hourlyRate);
        const isVisible = expanded.has(groupId);
        const functionFees: ImplementationFunctionFeeResponse[] =
          formRow?.implementationFunctionFeesResponse ??
          implementationActivityFee.implementationFunctionFeesResponse ??
          [];

        const childrenFeesMatrix: number[][] = functionFees.map((child) =>
          child.implementationYearlyFees.map((fee) => Number(fee.feePerUnit) || 0),
        );

        const parentFeesRaw: number[] =
          childrenFeesMatrix[0]?.map((_: any, yearIdx: number) =>
            childrenFeesMatrix.reduce(
              (sum: number, child: number[]) => sum + (child[yearIdx] ?? 0),
              0,
            ),
          ) ?? [];

        const parentRow: FeeRowVM = {
          groupId,
          isVisible,
          rowType: RowType.PARENT,
          implementationContainerDirectus: currentFee?.translations ?? [],
          hourlyRate: formHourlyRate,
          yearlyFees: parentFeesRaw.map((fee: number, i: number) => ({
            index: i + 1,
            feePerUnit: fee,
          })),
          totalAcrossScope: parentFeesRaw.reduce((a: number, b: number) => a + b, 0),
          comment: implementationActivityFee.comment,
          formGroupIndex: index,
        };

        const childRows: FeeRowVM[] = functionFees.map(
          (child: ImplementationFunctionFeeResponse, childIdx: number) => {
            const childTotal = child.implementationYearlyFees.reduce(
              (a: number, b: any) => a + (Number(b.feePerUnit) || 0),
              0,
            );
            return {
              groupId,
              isVisible,
              rowType: RowType.CHILD,
              implementationContainerDirectus:
                fnMap?.get(child.moduleFunctionDirectusId)?.translations ?? [],
              hourlyRate: null,
              yearlyFees: child.implementationYearlyFees,
              totalAcrossScope: childTotal,
              formGroupIndex: index,
              childIndex: childIdx,
            };
          },
        );

        const sumRow: FeeRowVM = {
          groupId,
          isVisible: true,
          rowType: RowType.SUM,
          implementationContainerDirectus: [],
          hourlyRate: null,
          yearlyFees: parentFeesRaw.map((fee: number, i: number) => ({
            index: i + 1,
            feePerUnit: fee * formHourlyRate,
          })),
          totalAcrossScope: parentFeesRaw.reduce(
            (a: number, b: number) => a + b * formHourlyRate,
            0,
          ),
        };

        return [parentRow, ...childRows, sumRow];
      },
    );
  });

  constructor() {
    super(ImplementationFeesComponent.FORM_CONTROLS);

    effect(() => {
      this.sumFeesModel.set(this.sumFees());
    });
  }

  protected createRowFormGroup(item: ImplementationActivityFeeResponse): FormGroup {
    return this.fb.group({
      implementationActivityDirectusUuid: [item.implementationActivityDirectusUuid],
      hourlyRate: [item.hourlyRate],
      implementationFunctionFeesResponse: this.fb.array(
        item.implementationFunctionFeesResponse.map((func) =>
          this.fb.group({
            moduleFunctionDirectusId: [func.moduleFunctionDirectusId],
            implementationYearlyFees: this.fb.array(
              func.implementationYearlyFees.map((year) =>
                this.fb.group({
                  index: [year.index],
                  feePerUnit: [year.feePerUnit],
                }),
              ),
            ),
          }),
        ),
      ),
      comment: [item.comment],
    });
  }

  readonly columnTotalsPerYear = computed<number[]>(() => {
    const rows = this.feeRows();
    const years = this.inputObject()?.scopeYears?.length || 0;
    const totals = new Array(years).fill(0);

    rows
      .filter((r) => this.isSum(r.rowType))
      .forEach((row: FeeRowVM) => {
        row.yearlyFees.forEach((feeObj: ImplementationYearlyFee, i: number) => {
          if (i < years) totals[i] += feeObj.feePerUnit;
        });
      });
    return totals;
  });

  readonly grandTotalAllYears = computed<number>(() => {
    return this.feeRows()
      .filter((row: FeeRowVM) => this.isSum(row.rowType))
      .reduce((acc: number, row: FeeRowVM) => acc + row.totalAcrossScope, 0);
  });

  readonly sumFees = computed<SumFees>(() => {
    return {
      columnTotalsPerYear: this.columnTotalsPerYear(),
      grandTotalAllYears: this.grandTotalAllYears(),
    } as SumFees;
  });

  sumFeesModel = model<SumFees>({} as SumFees);

  protected expandAll() {
    const allIds = new Set(this.feeRows().map((r) => r.groupId));
    this.expandedGroupIds.set(allIds);
  }

  protected collapseAll() {
    this.expandedGroupIds.set(new Set());
  }

  protected expandItem(row: FeeRowVM) {
    this.expandedGroupIds.update((prev) => {
      const next = new Set(prev);
      if (next.has(row.groupId)) {
        next.delete(row.groupId);
      } else {
        next.add(row.groupId);
      }
      return next;
    });
  }

  protected isParent(rowType: RowType) {
    return RowType.PARENT === rowType;
  }

  protected isSum(rowType: RowType) {
    return RowType.SUM === rowType;
  }

  protected isChild(rowType: RowType): boolean {
    return RowType.CHILD === rowType;
  }
}
